import React, { useState } from 'react';
import { RG_SCHEDULE, getDayLabel, normalizeCourt } from '../../data/rgSchedule';
import { Search } from 'lucide-react';
import { Input } from '../ui/input';

const COURT_COLORS = {
  'Court Philippe Chatrier': 'text-amber-400 bg-amber-400/10',
  'Court Suzanne Lenglen': 'text-emerald-400 bg-emerald-400/10',
  'Outside Courts': 'text-blue-400 bg-blue-400/10',
};

const SESSION_COLORS = {
  'Day Session': 'text-yellow-300 bg-yellow-300/10',
  'Evening Session': 'text-purple-400 bg-purple-400/10',
  'Day & Evening': 'text-pink-400 bg-pink-400/10',
};

const PHASE_COLORS = {
  '1st Round': 'text-zinc-300',
  '2nd Round': 'text-zinc-300',
  '3rd Round': 'text-cyan-400',
  '4th Round': 'text-cyan-400',
};

export const RGMatchesView = () => {
  const [search, setSearch] = useState('');
  const [filterCourt, setFilterCourt] = useState('ALL');
  const [filterSession, setFilterSession] = useState('ALL');

  // Group schedule by day
  const groupedByDay = {};
  RG_SCHEDULE.forEach(entry => {
    if (!groupedByDay[entry.day]) groupedByDay[entry.day] = [];
    groupedByDay[entry.day].push(entry);
  });

  const courts = ['ALL', 'Court Philippe Chatrier', 'Court Suzanne Lenglen', 'Outside Courts'];
  const sessions = ['ALL', 'Day Session', 'Evening Session'];

  // Filter
  const filteredDays = Object.entries(groupedByDay).map(([day, entries]) => {
    const filtered = entries.filter(e => {
      if (filterCourt !== 'ALL' && e.court !== filterCourt) return false;
      if (filterSession !== 'ALL') {
        if (filterSession === 'Day Session' && e.session !== 'Day Session' && e.session !== 'Day & Evening') return false;
        if (filterSession === 'Evening Session' && e.session !== 'Evening Session' && e.session !== 'Day & Evening') return false;
      }
      if (search) {
        const q = search.toLowerCase();
        const text = `day ${day} ${e.phase} ${e.session || ''} ${e.court} ${e.date}`.toLowerCase();
        if (!text.includes(q)) return false;
      }
      return true;
    });
    return { day: parseInt(day), entries: filtered };
  }).filter(d => d.entries.length > 0);

  return (
    <div className="space-y-4" data-testid="rg-matches-view">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Rechercher (jour, phase, court...)"
            className="pl-9 bg-zinc-800 border-zinc-700 text-white"
            data-testid="rg-matches-search"
          />
        </div>
        <div className="flex gap-1">
          {courts.map(c => (
            <button
              key={c}
              onClick={() => setFilterCourt(c)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                filterCourt === c
                  ? 'bg-[#C4A052] text-black'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              {c === 'ALL' ? 'Tous' : c === 'Court Philippe Chatrier' ? 'Chatrier' : c === 'Court Suzanne Lenglen' ? 'Lenglen' : 'Outside'}
            </button>
          ))}
        </div>
        <div className="flex gap-1">
          {sessions.map(s => (
            <button
              key={s}
              onClick={() => setFilterSession(s)}
              className={`px-3 py-1.5 rounded text-xs font-medium transition-colors ${
                filterSession === s
                  ? 'bg-[#C4A052] text-black'
                  : 'bg-zinc-800 text-zinc-400 hover:bg-zinc-700'
              }`}
            >
              {s === 'ALL' ? 'Toutes' : s === 'Day Session' ? 'Day' : 'Evening'}
            </button>
          ))}
        </div>
        <span className="text-xs text-zinc-500 ml-auto">
          {RG_SCHEDULE.length} matchs au total
        </span>
      </div>

      {/* Schedule table */}
      <div className="border border-zinc-700 rounded-lg overflow-hidden">
        <div className="grid grid-cols-[80px_1fr_1fr_1fr_1fr] gap-0 bg-zinc-800 border-b border-zinc-700 text-xs font-medium text-zinc-400">
          <div className="px-3 py-2.5">JOUR</div>
          <div className="px-3 py-2.5">DATE</div>
          <div className="px-3 py-2.5">PHASE</div>
          <div className="px-3 py-2.5">SESSION</div>
          <div className="px-3 py-2.5">COURT</div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {filteredDays.map(({ day, entries }) => (
            <div key={day}>
              {/* Day header */}
              <div className="bg-zinc-800/60 border-b border-zinc-700/50 px-3 py-2 flex items-center gap-2">
                <span className="text-[#C4A052] font-bold text-sm">Day {day}</span>
                <span className="text-zinc-500 text-xs">{getDayLabel(day)}</span>
                <span className="text-zinc-600 text-xs ml-auto">{entries.length} session(s)</span>
              </div>

              {entries.map((entry, idx) => {
                const phaseColor = PHASE_COLORS[entry.phase] || 'text-orange-400';
                return (
                  <div
                    key={idx}
                    className="grid grid-cols-[80px_1fr_1fr_1fr_1fr] gap-0 border-b border-zinc-800 hover:bg-zinc-800/40 text-sm"
                  >
                    <div className="px-3 py-2 text-[#C4A052] font-medium">
                      M{entry.day}
                    </div>
                    <div className="px-3 py-2 text-zinc-300">
                      {entry.date.substring(0, 5)}
                    </div>
                    <div className={`px-3 py-2 font-medium ${phaseColor}`}>
                      {entry.phase}
                    </div>
                    <div className="px-3 py-2">
                      {entry.session ? (
                        <span className={`px-2 py-0.5 rounded text-xs ${SESSION_COLORS[entry.session] || 'text-zinc-400'}`}>
                          {entry.session}
                        </span>
                      ) : (
                        <span className="text-zinc-600 text-xs">-</span>
                      )}
                    </div>
                    <div className="px-3 py-2">
                      <span className={`px-2 py-0.5 rounded text-xs ${COURT_COLORS[entry.court] || 'text-zinc-400'}`}>
                        {entry.court === 'Court Philippe Chatrier' ? 'Chatrier'
                          : entry.court === 'Court Suzanne Lenglen' ? 'Lenglen'
                          : 'Outside Courts'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default RGMatchesView;
